import React, { useState, useMemo, useEffect } from 'react'
import Icon from 'components/Icon/Icon'
import { useSelector, useDispatch } from 'react-redux'
import { selectCountryList } from 'state/constants'
import { selectTeamFilter, selectCountryFilter, setCountryFilter, setTeamFilter } from 'state/filter'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import { selectIsInternalUser, selectTeamList } from 'state/profile'
import { useIsRoleManagementValuesChanged, useNewTranslation } from 'hooks'
import Text from 'particles/Text'
import { useLocation, useHistory } from 'react-router-dom'
import _ from 'lodash'
import CreateTeam from 'components/Team/common/Modals/CreateTeam'
import { MODAL_TYPE } from 'components/Team/common/Modals'
import { COUNTRY_ENUM } from 'services/api/constants'
import Modal from 'particles/Modal'
import Menu from 'particles/Menu'
import MenuItem from 'particles/MenuItem'
import { NavIconButton } from './styles'
import { StyledButton } from 'components/Shared/Button/OldButton'
import { usePermission } from 'hooks/permissions'
import { OwnTeamManagement } from 'components/Shared/Permission/enums'

type ListProps = {
    text: string
    value: string | null
    isCreateTeam?: boolean
    icon?: any
    isDisabled?: boolean
    tooltipContext?: { label: string }
    onClick: (value: any) => void
}
interface FilterProps {
    id: string
    label: string
}
interface CountryFilterProps {
    className?: string
    isExtend?: boolean
    extendOnClick?: () => void
}

const Filter: React.FC<CountryFilterProps> = ({ className, isExtend, extendOnClick }) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
    const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        setMenuAnchorEl(event.currentTarget)
    }
    const handleCloseMenu = () => {
        setMenuAnchorEl(null)
    }
    const [showConfirmPopup, setShowConfirmPopup] = useState<any>({
        show: false,
        popupTitle: '',
        popupDescription: '',
        onConfirm: () => {
            return
        },
        onClose: () => {
            return
        },
    })
    const [showCreateTeam, setShowCreateTeam] = useState(false)
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const isInternalUser = useSelector(selectIsInternalUser)
    const countryList = useSelector(selectCountryList)
    const teamList = useSelector(selectTeamList)
    const countryFilter = useSelector(selectCountryFilter)
    const teamFilter = useSelector(selectTeamFilter)
    const isRoleManagementChanged = useIsRoleManagementValuesChanged()
    const isAbleToCreateTeam = usePermission(OwnTeamManagement.CreateTeam)
    const route = useLocation()
    const history = useHistory()

    const handleFilter = (value: string) => {
        if (extendOnClick) {
            extendOnClick()
        }
        let redirectPath = '/'
        let popupDescription = ''
        const isValueChange = isInternalUser
            ? value !== countryFilter
            : route.pathname.includes('/members') || route.pathname.includes('/brands')
              ? false
              : route.pathname.includes('/roles')
                ? isRoleManagementChanged && value !== teamFilter
                : value !== teamFilter

        if (
            isValueChange &&
            !['/campaigns', '/submissions', '/teams', '/banners/bannerGroups', '/ambassadors'].includes(route.pathname)
        ) {
            if (
                [
                    '/campaign/view/tasks',
                    '/campaign/edit/tasks',
                    '/campaign/edit/registration',
                    '/campaign/edit/publish',
                    '/campaign/edit/payment',
                ].includes(route.pathname)
            ) {
                redirectPath = '/campaigns'
                popupDescription = isInternalUser
                    ? t('modalWindow.confirmationMessage.changeCountryCampaignSubtitle')
                    : t('modalWindow.confirmationMessage.changeTeamSubtitle')
            } else if (['/banners/bannerList', '/banners/editBanner'].includes(route.pathname)) {
                redirectPath = '/banners/bannerGroups'
                popupDescription = t('modalWindow.confirmationMessage.changeCountryAllBannersSubtitle')
            } else if (
                [
                    '/submissions/progress',
                    '/submissions/view-performance',
                    '/submissions/payouts',
                    '/submissions/review',
                ].includes(route.pathname)
            ) {
                redirectPath = '/submissions'
                popupDescription = isInternalUser
                    ? t('modalWindow.confirmationMessage.changeCountrySubmissionSubtitle')
                    : t('modalWindow.confirmationMessage.changeTeamSubmissionSubtitle')
            } else if (
                route.pathname.includes('/members') ||
                route.pathname.includes('/brands') ||
                route.pathname.includes('roles')
            ) {
                redirectPath = route.pathname.includes('roles') && !isInternalUser ? `/teams/${value}/roles` : '/teams'
                popupDescription = isInternalUser
                    ? t('modalWindow.confirmationMessage.changeCountryTeamSubtitle')
                    : t('modalWindow.confirmationMessage.changeTeamExternalTeamSubtitle')
            }
            setShowConfirmPopup({
                ...showConfirmPopup,
                show: true,
                popupTitle: isInternalUser
                    ? t('modalWindow.confirmationMessage.changeCountryTitle')
                    : t('modalWindow.confirmationMessage.changeTeamTitle'),
                popupDescription: popupDescription,
                onConfirm: () => {
                    history.push(redirectPath)
                    setShowConfirmPopup(false)
                    if (isInternalUser) {
                        dispatch(setCountryFilter(value))
                    } else {
                        dispatch(setTeamFilter(value))
                    }
                },
            })
        } else {
            if (isInternalUser) {
                dispatch(setCountryFilter(value))
            } else {
                if (route.pathname.includes('/members')) {
                    history.push(`/team/${value}/members`)
                }
                if (route.pathname.includes('/brands')) {
                    history.push(`/team/${value}/brands`)
                }
                if (route.pathname.includes('/roles')) {
                    if (value !== teamFilter) {
                        history.push(`/teams/${value}/roles`)
                    }
                }
                dispatch(setTeamFilter(value))
            }
        }
    }
    const changeFilterTranslate = (countryFilter: string | null) => {
        if (!isInternalUser) {
            const selectTeam = teamList.find(team => team.id === teamFilter)
            if (selectTeam) {
                return selectTeam.label
            } else {
                return null
            }
        } else {
            switch (countryFilter) {
                case COUNTRY_ENUM.ID:
                    return t('country.indonesia')
                case COUNTRY_ENUM.SG:
                    return t('country.singapore')
                case COUNTRY_ENUM.TW:
                    return t('country.taiwan')
                case COUNTRY_ENUM.MY:
                    return t('country.malaysia')
                case COUNTRY_ENUM.TH:
                    return t('country.thailand')
                case COUNTRY_ENUM.VN:
                    return t('country.vietnam')
                case COUNTRY_ENUM.HK:
                    return t('country.hongKong')
                case COUNTRY_ENUM.PH:
                    return t('country.philippines')
                default:
                    return t('country.allCountries')
            }
        }
    }
    const mappingFilterList = (filterList: FilterProps[]) => {
        const list: ListProps[] = []
        filterList.forEach((filter: FilterProps) => {
            const option = {
                text: filter.label,
                value: filter.id,
                onClick: handleFilter,
            }
            list.push(option)
        })
        if (isInternalUser) {
            list.push({ text: t('country.allCountries'), value: '', onClick: handleFilter })
        }
        if (!isInternalUser) {
            list.push({
                text: t('button.createTeam'),
                value: null,
                isCreateTeam: true,
                isDisabled: !isAbleToCreateTeam,
                tooltipContext: { label: t('manageTeam.createTeamButtonDisableTooltips') },
                onClick: () => {
                    setShowCreateTeam(true)
                },
            })
        }
        return list
    }

    const filterLists = useMemo(() => {
        const filterList = isInternalUser ? countryList : teamList
        return mappingFilterList(filterList)
    }, [countryList, teamFilter, t, isAbleToCreateTeam, teamList])

    const currentFilter = useMemo(
        () => changeFilterTranslate(countryFilter),
        [countryFilter, teamFilter, isInternalUser, teamList, t],
    )

    useEffect(() => {
        if (!isInternalUser && !teamFilter && teamList.length > 0) {
            dispatch(setTeamFilter(teamList[0].id))
        }
    }, [isInternalUser, teamFilter, teamList])

    return (
        <>
            <div className={className}>
                {isExtend ? (
                    <div className="extend-filter">
                        <div className="dropdown-control" onClick={handleOpenMenu}>
                            <div className="dropdown-control-left">
                                {isInternalUser ? (
                                    <Icon.CountryIcon width={20} height={20} />
                                ) : (
                                    <Icon.TeamIcon width={20} height={20} />
                                )}
                                <Text>{currentFilter}</Text>
                            </div>
                            <Icon.DownArrowIcon />
                        </div>
                    </div>
                ) : (
                    <NavIconButton label={currentFilter ?? ''} onClick={handleOpenMenu}>
                        <StyledButton width="36px" height="36px" data-testid="test-country-team-button">
                            {isInternalUser ? (
                                <Icon.CountryIcon width={20} height={20} />
                            ) : (
                                <Icon.TeamIcon width={20} height={20} />
                            )}
                        </StyledButton>
                    </NavIconButton>
                )}
                <Menu
                    size="m"
                    open={Boolean(menuAnchorEl)}
                    anchorEl={menuAnchorEl}
                    onClose={handleCloseMenu}
                    {...(isExtend
                        ? { PaperProps: { sx: { width: 'calc(200% - 1rem)' } } }
                        : {
                              width: 224,
                              transformOrigin: { vertical: 'center', horizontal: 'left' },
                              anchorOrigin: {
                                  vertical: 'center',
                                  horizontal: 'right',
                              },
                              sx: { marginLeft: '30px' },
                          })}
                >
                    {filterLists.map((option, index) => {
                        const components = []

                        if (option.isCreateTeam) {
                            components.push(<MenuItem.Divider />)
                        }
                        components.push(
                            <MenuItem.Text
                                key={`menu-item-filter-${index}`}
                                text={option.text}
                                onClick={() => {
                                    handleCloseMenu()
                                    option.onClick(option.value)
                                }}
                                selected={option.text === currentFilter}
                                icon={option.isCreateTeam ? 'plus' : undefined}
                                isDisabled={option.isDisabled}
                                tooltipContext={option.tooltipContext}
                            />,
                        )

                        return components
                    })}
                </Menu>

                {showConfirmPopup.show && (
                    <Modal
                        title={showConfirmPopup.popupTitle}
                        subtext={showConfirmPopup.popupDescription}
                        secondaryButtonProps={{
                            label: t('button.noGoBack'),
                        }}
                        primaryButtonProps={{
                            label: t('button.yesChangeNow'),
                        }}
                        size="s"
                        onConfirm={showConfirmPopup.onConfirm}
                        onClose={() => setShowConfirmPopup(false)}
                        isClosable={false}
                    />
                )}
            </div>
            {showCreateTeam && (
                <CreateTeam
                    type={MODAL_TYPE.CREATE_TEAM}
                    onClose={() => {
                        setShowCreateTeam(false)
                    }}
                />
            )}
        </>
    )
}

export default styled(Filter)`
    margin-bottom: 20px;
    .extend-filter {
        margin-top: 20px;
    }
    .dropdown-control {
        display: flex;
        height: 36px;
        cursor: pointer;
        padding: 8px 7px 8px 4px;
        box-sizing: border-box;
        justify-content: space-between;
        align-items: center;
        background-color: transparent;
        color: ${theme.newColors.neutral700};
        font-weight: ${theme.fontWeight.medium};
        border-radius: 4px;
        border: 1px solid ${theme.newColors.neutral200};
        .dropdown-control-left {
            display: flex;
            gap: 4px;
            align-items: center;
        }
    }
    .popup-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 0 24px;
        text-align: center;
        gap: 8px;
        .popup-description {
            div {
                display: inline;
            }
        }
    }
`
